import {Group} from '@visx/group';
import {ScaleSVG} from '@visx/responsive';
import {Pie} from '@visx/shape';
import {useTooltip, useTooltipInPortal} from '@visx/tooltip';

import {ChartTooltip} from '../ChartTooltip';
import {TOOLTIP_DEBOUNCE} from '../utils/constants';

import {PieArc} from './PieArc';

import type {PieBarDatum} from '../utils/types';

// Define a fixed viewBox size for consistent scaling
const viewBoxWidth = 400;
const viewBoxHeight = 400;

type Props<T extends PieBarDatum> = {
  data: T[];
  handleDatumClick?: (item: T) => void;
  renderTooltipLabel?: (item: T) => {
    title: React.ReactNode;
    value: React.ReactNode;
  };
};
export function PieChart<T extends PieBarDatum>({
  data,
  handleDatumClick,
  renderTooltipLabel,
}: Props<T>): React.JSX.Element {
  const tooltipHelpers = useTooltip<T>();
  const {containerRef, TooltipInPortal} = useTooltipInPortal({
    scroll: true,
    debounce: TOOLTIP_DEBOUNCE,
  });

  const radius = Math.min(viewBoxWidth, viewBoxHeight) / 2;

  return (
    <div
      className="relative aspect-square w-full max-w-md grow"
      ref={containerRef}
    >
      <ScaleSVG width={viewBoxWidth} height={viewBoxHeight}>
        <Group top={viewBoxHeight / 2} left={viewBoxWidth / 2}>
          <Pie
            data={data}
            pieValue={({value}) => value}
            outerRadius={radius}
            innerRadius={radius * 0.4}
            cornerRadius={3}
            padAngle={0.02}
          >
            {(pie) =>
              pie.arcs.map((arc, arcIndex) => (
                <PieArc
                  key={arc.data.id}
                  pie={pie}
                  idx={arcIndex + 1}
                  pieArcDatum={arc}
                  pieChartDatum={data[arcIndex]}
                  handleDatumClick={handleDatumClick}
                  tooltipHelpers={tooltipHelpers}
                />
              ))
            }
          </Pie>
        </Group>

        {tooltipHelpers.tooltipOpen &&
          tooltipHelpers.tooltipData &&
          renderTooltipLabel && (
            <ChartTooltip
              Component={TooltipInPortal}
              helpers={tooltipHelpers}
              renderTooltipLabel={renderTooltipLabel}
            />
          )}
      </ScaleSVG>
    </div>
  );
}

PieChart.displayName = 'PieChart';
